(function () {
  'use strict';
  const text = (node, value) => { if (node) node.textContent = value; };
  const integer = value => Number.isSafeInteger(value) && value >= 0;
  const keyFor = options => JSON.stringify(options);

  class EverteeOwnedBundle extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;
      this.version = 0;
      this.busy = false;
      this.size = null;
      this.rows = [];
      this.pendingKey = 'evertee-owned-pending-v1:' + this.dataset.productId;
      this.form = this.querySelector('form[data-evertee-owned-bundle-form]');
      this.wallet = this.querySelector('[data-owned-wallet]');
      this.addButton = this.querySelector('[data-owned-add]');
      this.walletPrompt = this.querySelector('[data-owned-wallet-prompt]');
      this.status = this.querySelector('[data-owned-status]');
      this.sizeError = this.querySelector('[data-owned-size-error]');
      this.labels = JSON.parse(this.querySelector('[data-owned-labels]').textContent);
      this.colourImages = JSON.parse(this.querySelector('[data-owned-colour-images]')?.textContent || '{}');
      this.addEventListener('click', event => this.onClick(event));
      this.addEventListener('change', event => event.stopPropagation());
      this.addEventListener('input', event => event.stopPropagation());
      this.form.addEventListener('submit', event => this.onSubmit(event));
      this.wallet.addEventListener('click', event => {
        try {
          this.assertNativeReady();
        } catch (error) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.lock();
          this.purchaseError(error);
        }
      }, true);
      this.visibilityHandler = () => {
        if (!document.hidden && this.catalog) this.refresh();
      };
      document.addEventListener('visibilitychange', this.visibilityHandler);
      this.lock();
      this.load();
    }

    disconnectedCallback() {
      this.controller?.abort();
      clearTimeout(this.expiryTimer);
      document.removeEventListener('visibilitychange', this.visibilityHandler);
    }

    normaliseOptions(options) {
      return options.length === 2 ? [...options, 'Standard'] : options;
    }

    say(name) { return this.labels[name] || this.labels.retry; }

    ownedPricingActive() {
      return this.dataset.ownedPricingActive === 'true' && this.dataset.ownedPricingVersion === '1' &&
        this.catalog?.pricing?.engine === 'evertee-owned-v1' && this.pricingState?.active === true;
    }

    readPricingState(value) {
      if (!value || typeof value.active !== 'boolean' || value.version !== '1' ||
          value.productId !== this.dataset.productId || value.currency !== this.dataset.currency ||
          typeof value.revision !== 'string' || !value.revision || value.revision.length > 128 ||
          /[|\r\n]/.test(value.revision) || typeof value.freePremium !== 'boolean' ||
          !Array.isArray(value.prices) || value.prices.length !== this.counts.length) throw new Error('pricing');
      const hasPremium = Boolean(this.finishMap.premiumBlank);
      const freePremium = value.freePremium && ['9886457856266', '10630781042954'].includes(this.dataset.productId);
      const prices = this.counts.map(count => {
        const matches = value.prices.filter(price => price.count === count);
        if (matches.length !== 1) throw new Error('pricing');
        const price = matches[0];
        if (!integer(price.standard) || price.standard <= 0 ||
            price.standard > this.catalog.pricing.unitCentsByFinish[this.finishMap.standardBlank] * count ||
            (hasPremium && (!integer(price.premium) || price.premium <= 0 ||
              price.premium > this.catalog.pricing.unitCentsByFinish[this.finishMap.premiumBlank] * count)) ||
            (!hasPremium && price.premium != null) ||
            (freePremium && price.premium !== price.standard)) throw new Error('pricing');
        return {count, standard: price.standard, ...(hasPremium ? {premium: price.premium} : {})};
      });
      return {active: value.active, version: '1', productId: value.productId, currency: value.currency,
        revision: value.revision, freePremium: value.freePremium, prices};
    }

    freePremiumActive() {
      return this.pricingState?.active === true && this.pricingState.freePremium &&
        ['9886457856266', '10630781042954', '10043716010250', '10631218200842'].includes(this.dataset.productId) &&
        this.pricingState.prices.every(price => price.premium === price.standard);
    }

    assertCurrentPricing(value) {
      const current = this.readPricingState(value);
      if (!current.active) throw new Error('pricing');
      if (JSON.stringify(current) !== JSON.stringify(this.pricingState)) throw new Error('pricing-changed');
    }

    newPending() {
      const next = {id: window.EverteeBundleCore.createBundleId(window.crypto), state: 'prepared'};
      this.savePending(next);
    }

    readPending() {
      const saved = window.sessionStorage.getItem(this.pendingKey);
      if (!saved) { this.newPending(); return; }
      const value = JSON.parse(saved);
      window.EverteeBundleCore.bundleProperties(value.id, 1);
      if (!['prepared', 'submitting', 'uncertain', 'rejected'].includes(value.state) ||
          (value.state !== 'prepared' && (!Array.isArray(value.items) || !value.items.length ||
          !Number.isSafeInteger(value.totalCents) || value.currency !== this.dataset.currency))) {
        throw new Error('pending-storage');
      }
      if (value.items && (!window.EverteeBundleCore.exactItems(value.items, value.items) ||
          value.items.some(item => item.properties._et_bundle_id !== value.id))) throw new Error('pending-storage');
      this.pending = value;
    }

    savePending(value = this.pending) {
      window.sessionStorage.setItem(this.pendingKey, JSON.stringify(value));
      this.pending = value;
    }

    rememberAttempt(items, totalCents) {
      this.pending = {...this.pending, state: 'submitting', items, totalCents, currency: this.dataset.currency};
      this.savePending();
    }

    async fetchCart(signal) {
      const url = new URL(this.dataset.cartUrl + '.js', location.origin);
      if (url.origin !== location.origin) throw new Error('cart-url');
      const response = await fetch(url.href, {credentials: 'same-origin', cache: 'no-store', signal,
        headers: {Accept: 'application/json'}});
      if (!response.ok) throw new Error('cart-fetch');
      return response.json();
    }

    async recoverAttempt() {
      if (!this.pending || this.pending.state === 'prepared') return false;
      const cart = await this.fetchCart();
      const result = window.EverteeBundleCore.inspectCartGroup(cart, this.pending.items,
        this.pending.totalCents, this.pending.currency);
      if (result.state === 'exact') {
        this.newPending();
        return true;
      }
      if (result.state === 'mismatch') throw new Error('bundle-cart-mismatch');
      if (this.pending.state !== 'rejected') throw new Error('bundle-add-uncertain');
      this.pending = {id: this.pending.id, state: 'prepared'};
      this.savePending();
      return false;
    }

    purchaseError(error) {
      if (['bundle-cart-mismatch', 'bundle-add-uncertain'].includes(error.message)) {
        text(this.status, this.say(error.message === 'bundle-cart-mismatch' ? 'bundleNeedsReview' : 'addUnconfirmed') + ' ');
        const link = document.createElement('a');
        link.href = this.dataset.cartUrl;
        link.textContent = this.say('reviewBag');
        this.status.append(link);
        if (this.pending?.state !== 'prepared') {
          const retry = document.createElement('button');
          retry.type = 'button';
          retry.className = 'button button--secondary';
          retry.dataset.ownedReviewComplete = '';
          retry.textContent = this.say('bagReviewed');
          this.status.append(document.createTextNode(' '), retry);
        }
      } else text(this.status, error.message === 'pricing-changed' ? this.say('pricingChanged') :
        error.message === 'pricing' ? this.say('pricingUnverified') : this.say('addFailed'));
    }

    async recoverAfterBagReview() {
      if (this.busy || !this.pending?.items) return;
      this.busy = true;
      this.setBusy(true);
      this.lock();
      try {
        const cart = await this.fetchCart();
        const group = window.EverteeBundleCore.inspectCartGroup(cart, this.pending.items,
          this.pending.totalCents, this.pending.currency);
        if (group.state === 'mismatch') throw new Error('bundle-cart-mismatch');
        if (group.state === 'exact') this.newPending();
        else {
          this.pending = {id: this.pending.id, state: 'prepared'};
          this.savePending();
        }
        if (await this.refresh()) text(this.status, this.say(group.state === 'exact' ? 'added' : 'readyToRetry'));
      } catch (error) {
        this.purchaseError(error.message === 'bundle-cart-mismatch' ? error : new Error('bundle-add-uncertain'));
      } finally {
        this.busy = false;
        this.setBusy(false);
      }
    }

    assertNativeReady() {
      if (!this.ready || this.busy || !this.ownedPricingActive() || this.pricingExpired() ||
          this.pending?.state !== 'prepared') throw new Error('pricing');
      const action = new URL(this.form.action, location.origin);
      const expected = new URL(this.dataset.cartAddUrl, location.origin);
      if (action.origin !== location.origin || action.pathname !== expected.pathname || action.search !== expected.search || action.hash ||
          this.form.method.toLowerCase() !== 'post') throw new Error('native-form');
      window.EverteeBundleCore.assertNativeEntries(this.form, this.bundle.toNativeFormEntries());
    }

    lock() {
      this.ready = false;
      this.bundle = null;
      this.wallet.hidden = true;
      this.wallet.inert = true;
      this.walletPrompt.hidden = true;
      const total = this.querySelector('[data-owned-total-wrap]');
      if (total) total.hidden = true;
      window.EverteeBundleCore?.clearNativeForm(this.form);
    }

    async prepareCatalog() {}

    async load() {
      try {
        const url = new URL(this.dataset.catalogUrl, location.origin);
        if (!['https:', 'http:'].includes(url.protocol)) throw new Error('catalog-url');
        const response = await fetch(url.href, {credentials: 'same-origin'});
        if (!response.ok) throw new Error('catalog-fetch');
        const catalog = await response.json();
        if (String(catalog.productId) !== this.dataset.productId || !Array.isArray(catalog.variants) ||
            catalog.variants.length !== Number(this.dataset.variantCount)) throw new Error('catalog-identity');
        catalog.variants = catalog.variants.map(variant => ({...variant, options: this.normaliseOptions(variant.options)}));
        this.catalog = catalog;
        await this.prepareCatalog(catalog);
        this.readPending();
        if (catalog.labels) Object.assign(this.labels, catalog.labels);
        this.counts = JSON.parse(this.dataset.allowedCounts);
        this.count = Number(this.dataset.initialCount);
        this.colours = catalog.colours;
        this.visibleColours = catalog.visibleColours || catalog.colours.filter(colour => !['Pale Blue', 'Ash Rose', 'Sand'].includes(colour));
        this.sizes = catalog.sizes;
        this.finishes = catalog.finishes;
        this.finishMap = catalog.finishMap;
        if (!this.finishMap?.standardBlank || !this.finishes.includes(this.finishMap.standardBlank)) throw new Error('finish-map');
        this.pricingState = this.readPricingState(JSON.parse(this.querySelector('[data-owned-pricing-state]').textContent));
        this.logo = false;
        this.premium = this.freePremiumActive();
        this.finish = this.finishMap[this.premium ? 'premiumBlank' : 'standardBlank'];
        this.index = new Map(catalog.variants.map(variant => [keyFor(variant.options), variant]));
        this.rows = Array.from({length: this.count}, () => catalog.initialColour || null);
        this.makeBundle(catalog.variants);
        this.watchExpiry();
        this.renderSizes();
        this.renderCounts();
        this.renderRows();
        this.renderFinish();
        text(this.status, '');
        this.addButton.disabled = false;
        this.walletPrompt.disabled = false;
        this.dataset.loaded = 'true';
      } catch (error) {
        this.lock();
        text(this.status, this.say('retry'));
        this.dataset.loadError = 'true';
      }
    }

    makeBundle(variants) {
      const bundle = window.EverteeBundleCore.createBundle({
        variants, colours: this.colours, sizes: this.sizes, finishes: this.finishes,
        allowedCounts: this.counts, initialCount: this.count, initialColour: null,
        initialFinish: this.finish, allowedProperties: window.EverteeBundleCore.bundlePropertyKeys
      });
      this.rows.slice(0, this.count).forEach((colour, index) => bundle.setColour(index, colour));
      if (this.size !== null) bundle.setSize(this.size);
      bundle.setProperties(window.EverteeBundleCore.bundleProperties(this.pending.id, this.count));
      return bundle;
    }

    button(value, label, attribute, className) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = className;
      button.dataset[attribute] = String(value);
      button.setAttribute('aria-pressed', 'false');
      button.textContent = label;
      return button;
    }

    renderSizes() {
      const target = this.querySelector('[data-owned-sizes]');
      target.replaceChildren(...this.sizes.map(size => this.button(size, size, 'ownedSize', 'et-owned-pill')));
    }

    renderCounts() {
      const target = this.querySelector('[data-owned-counts]');
      target.replaceChildren(...this.counts.map(count => {
        const selected = count === this.count;
        const tier = this.catalog.tiers[String(count)];
        const card = document.createElement('div');
        card.className = 'et-owned-card';
        card.dataset.selected = String(selected);
        const label = count === 1 ? this.say('bundleSingle') : this.say('bundlePlural').replaceAll('__ET_COUNT__', String(count));
        const button = this.button(count, '', 'ownedCount', 'et-owned-pack');
        button.setAttribute('aria-pressed', String(selected));
        button.setAttribute('aria-expanded', String(selected));
        button.setAttribute('aria-controls', this.id + '-colours-' + count);
        const radio = document.createElement('span');
        radio.className = 'et-owned-radio';
        radio.setAttribute('aria-hidden', 'true');
        const content = document.createElement('span');
        content.className = 'et-owned-card-content';
        const titleLine = document.createElement('span');
        titleLine.className = 'et-owned-card-title-line';
        const title = document.createElement('span');
        title.className = 'et-owned-card-title';
        title.textContent = label;
        titleLine.append(title);
        const total = this.cardTotal(count);
        if (tier.badge) {
          const badge = document.createElement('span');
          badge.className = 'et-owned-badge';
          badge.textContent = tier.badge === 'mostPopular' ? this.say('mostPopular') : this.say('perTee').replaceAll('__ET_PRICE__', this.money(Math.floor(total / count)));
          titleLine.append(badge);
        }
        content.append(titleLine);
        if (tier.usualBaseCents) {
          const usual = document.createElement('span');
          usual.className = 'et-owned-usual';
          usual.textContent = this.say('usually').replaceAll('__ET_PRICE__', this.money(tier.usualBaseCents + (this.premium ? count * this.catalog.pricing.premiumExtraCents : 0), true));
          content.append(usual);
        }
        const price = document.createElement('span');
        price.className = 'et-owned-price';
        price.textContent = this.money(total);
        button.append(radio, content, price);
        card.append(button);
        if (tier.ribbon) {
          const ribbon = document.createElement('span');
          ribbon.className = 'et-owned-ribbon';
          ribbon.textContent = this.say(tier.ribbon);
          card.append(ribbon);
        }
        const rows = document.createElement('div');
        rows.id = this.id + '-colours-' + count;
        rows.className = 'et-owned-rows';
        rows.hidden = !selected;
        if (selected) rows.dataset.ownedRows = '';
        card.append(rows);
        return card;
      }));
    }

    money(cents, trim = false) {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency', currency: this.dataset.currency,
        minimumFractionDigits: trim ? 0 : 2, maximumFractionDigits: 2
      }).format(cents / 100);
    }

    cardTotal(count) {
      const price = this.pricingState?.prices.find(price => price.count === count);
      const total = price?.[this.premium ? 'premium' : 'standard'];
      if (!integer(total)) throw new Error('pricing');
      return total;
    }

    renderRows() {
      const target = this.querySelector('[data-owned-rows]');
      target.replaceChildren(...this.rows.slice(0, this.count).map((selected, index) => {
        const field = document.createElement('div');
        field.className = 'et-owned-colour-row';
        field.setAttribute('role', 'group');
        const legend = document.createElement('div');
        legend.className = 'et-owned-colour-label';
        legend.id = this.id + '-colour-label-' + index;
        field.setAttribute('aria-labelledby', legend.id);
        legend.textContent = this.say('colourLabel').replaceAll('__ET_COLOUR__', selected || '');
        const number = document.createElement('span');
        number.className = 'et-owned-row-number';
        number.textContent = '#' + (index + 1);
        number.setAttribute('aria-hidden', 'true');
        const content = document.createElement('div');
        content.className = 'et-owned-row-content';
        const choices = document.createElement('div');
        choices.className = 'et-owned-colours';
        this.visibleColours.forEach(colour => {
          const button = this.button(colour, colour, 'ownedColour', 'et-owned-colour');
          button.dataset.ownedRow = String(index);
          button.setAttribute('aria-pressed', String(colour === selected));
          button.setAttribute('aria-label', this.say('teeNumber').replaceAll('__ET_NUMBER__', String(index + 1)) + ': ' + colour);
          const swatch = this.catalog.swatches?.[colour];
          if (swatch?.color && CSS.supports('color', swatch.color)) {
            button.classList.add('et-owned-colour--swatch');
            button.style.setProperty('--owned-swatch', swatch.color);
            button.title = colour;
            const label = document.createElement('span');
            label.className = 'visually-hidden';
            label.textContent = colour;
            button.replaceChildren(label);
          }
          choices.appendChild(button);
        });
        content.append(legend, choices);
        field.append(number, content);
        return field;
      }));
    }

    updateColourImage(colour) {
      const source = this.colourImages?.[colour];
      if (!source) return;
      const gallery = this.closest('product-info')?.querySelector('media-gallery');
      if (typeof gallery?.setColourImage === 'function') gallery.setColourImage(source, colour);
    }

    renderFinish() {
      this.querySelectorAll('[data-owned-logo]').forEach(button => {
        button.setAttribute('aria-pressed', String((button.dataset.ownedLogo === 'true') === this.logo));
      });
      const premium = this.querySelector('[data-owned-premium]');
      premium?.setAttribute('aria-checked', String(this.premium));
      premium?.setAttribute('aria-label', this.say(this.freePremiumActive() ? 'premiumFreeTitle' : 'premiumTitle'));
      text(this.querySelector('[data-owned-premium-title]'), this.say(this.freePremiumActive() ? 'premiumFreeTitle' : 'premiumTitle'));
      text(this.querySelector('[data-owned-premium-price]'), this.say(this.freePremiumActive() ? 'premiumFreePrice' : 'premiumPrice'));

    }

    onClick(event) {
      const button = event.target.closest('button');
      if (!button || !this.contains(button)) return;
      if (button.hasAttribute('data-owned-review-complete')) {
        event.preventDefault();
        event.stopPropagation();
        if (event.isTrusted) this.recoverAfterBagReview();
        return;
      }
      if (button.matches('[data-owned-add], [data-owned-wallet-prompt]')) {
        event.stopPropagation();
        if (button.matches('[data-owned-wallet-prompt]')) {
          event.preventDefault();
          if (!this.size) this.showSizeError();
          else this.refresh();
        }
        return;
      }
      const isChoice = button.matches('[data-owned-size], [data-owned-colour], [data-owned-count], [data-owned-logo], [data-owned-premium]');
      if (!isChoice) return;
      event.preventDefault();
      event.stopPropagation();
      if (!event.isTrusted || !this.catalog || this.busy) return;
      if (this.pending?.state !== 'prepared') { this.purchaseError(new Error('bundle-add-uncertain')); return; }
      if (button.dataset.ownedSize !== undefined) {
        this.size = button.dataset.ownedSize;
        this.sizeError.hidden = true;
        this.querySelector('[data-owned-size-field]').classList.remove('et-owned-size-required');
        this.querySelectorAll('[data-owned-size]').forEach(node => {
          node.setAttribute('aria-pressed', String(node.dataset.ownedSize === this.size));
          node.removeAttribute('aria-invalid');
        });
      } else if (button.dataset.ownedColour !== undefined) {
        const rowIndex = Number(button.dataset.ownedRow);
        this.rows[rowIndex] = button.dataset.ownedColour;
        const field = button.closest('.et-owned-colour-row');
        field.querySelectorAll('[data-owned-colour]').forEach(choice => {
          choice.setAttribute('aria-pressed', String(choice.dataset.ownedColour === button.dataset.ownedColour));
        });
        text(field.querySelector('.et-owned-colour-label'), this.say('colourLabel').replaceAll('__ET_COLOUR__', button.dataset.ownedColour));
        this.updateColourImage(button.dataset.ownedColour);
      } else if (button.dataset.ownedCount !== undefined) {
        this.count = Number(button.dataset.ownedCount);
        while (this.rows.length < this.count) this.rows.push(this.catalog.initialColour || null);
        this.renderCounts();
        this.renderRows();
        this.querySelector('[data-owned-count="' + this.count + '"]')?.focus({preventScroll: true});
      } else {
        if (button.dataset.ownedLogo !== undefined) this.logo = button.dataset.ownedLogo === 'true';
        else this.premium = !this.premium;
        this.finish = this.finishMap[(this.premium ? 'premium' : 'standard') + (this.logo ? 'Logo' : 'Blank')];
        this.renderFinish();
        this.renderCounts();
        this.renderRows();
        if (button.hasAttribute('data-owned-premium') && this.premium &&
            !matchMedia('(prefers-reduced-motion: reduce)').matches &&
            typeof window.showPremiumCongratulationsOverlay === 'function') {
          window.showPremiumCongratulationsOverlay();
        }
      }
      this.refresh();
    }

    showSizeError() {
      this.sizeError.hidden = false;
      const field = this.querySelector('[data-owned-size-field]');
      field.classList.add('et-owned-size-required');
      const first = field.querySelector('button');
      first?.setAttribute('aria-invalid', 'true');
      first?.setAttribute('aria-describedby', this.sizeError.id);
      field.scrollIntoView({behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center'});
      first?.focus({preventScroll: true});
    }

    selectedVariants() {
      if (!this.size) throw new Error('size');
      if (!this.finishes.includes(this.finish)) throw new Error('finish');
      const found = new Map();
      this.rows.slice(0, this.count).forEach(colour => {
        if (!colour) throw new Error('colour');
        const variant = this.index.get(keyFor([colour, this.size, this.finish]));
        if (!variant) throw new Error('variant');
        found.set(String(variant.id), variant);
      });
      return [...found.values()];
    }

    async fetchVariant(variant, signal) {
      const url = new URL(this.dataset.productUrl, location.origin);
      url.searchParams.set('variant', variant.id);
      url.searchParams.set('section_id', this.dataset.variantSection || this.dataset.sectionId);
      const preview = new URL(location.href).searchParams.get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
      const response = await fetch(url.href, {credentials: 'same-origin', signal, cache: 'no-store'});
      if (!response.ok) throw new Error('variant-fetch');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const node = html.querySelector('[data-et-owned-selected-variant]');
      if (!node) throw new Error('variant-data');
      const current = JSON.parse(node.textContent);
      if (current.variant) current.variant.options = this.normaliseOptions(current.variant.options);
      this.assertCurrentPricing(current.ownedPricing);
      if (String(current.productId) !== this.dataset.productId || current.currency !== this.dataset.currency || !current.variant ||
          String(current.variant.id) !== String(variant.id) ||
          keyFor(current.variant.options) !== keyFor(variant.options) ||
          typeof current.variant.available !== 'boolean' || !integer(current.variant.price)) throw new Error('variant-mismatch');
      if (current.variant.price !== this.catalog.pricing.unitCentsByFinish[this.finish]) throw new Error('pricing');
      return {...current.variant, id: String(current.variant.id)};
    }

    pricingTotal(bundle, variants) {
      const pricing = this.catalog.pricing;
      if (!this.ownedPricingActive() || this.dataset.pricingReady !== 'true' || pricing?.verified !== true ||
          (pricing.currency || this.catalog.currency) !== this.dataset.currency || this.pricingExpired() ||
          !integer(pricing.discountCentsByCount?.[this.count])) throw new Error('pricing');
      const prices = new Map(variants.map(variant => [String(variant.id), variant.price]));
      const gross = bundle.resolve().items.reduce((sum, line) => sum + prices.get(line.id) * line.quantity, 0);
      const total = this.cardTotal(this.count);
      if (!integer(gross) || !integer(total) || total > gross) throw new Error('pricing');
      return total;
    }

    pricingExpired() {
      if (this.ownedPricingActive() && !this.catalog.pricing.endsAt) return false;
      const ends = Date.parse(this.catalog?.pricing?.endsAt || '');
      return !Number.isFinite(ends) || Date.now() >= ends;
    }

    watchExpiry() {
      clearTimeout(this.expiryTimer);
      const remaining = Date.parse(this.catalog?.pricing?.endsAt || '') - Date.now();
      if (!(remaining > 0)) return;
      this.expiryTimer = setTimeout(() => {
        if (this.pricingExpired()) {
          ++this.version;
          this.controller?.abort();
          this.lock();
          text(this.status, this.say('pricingUnverified'));
        } else this.watchExpiry();
      }, Math.min(remaining, 2147483647));
    }

    async refresh() {
      const version = ++this.version;
      this.controller?.abort();
      this.controller = new AbortController();
      this.lock();
      this.querySelector('[data-owned-total-wrap]').hidden = true;
      if (!this.catalog || !this.size) { text(this.status, ''); return false; }
      text(this.status, this.say('checking'));
      try {
        if (!this.ownedPricingActive()) throw new Error('pricing');
        if (this.pending?.state !== 'prepared') throw new Error('bundle-add-uncertain');
        const selected = this.selectedVariants();
        const fresh = await Promise.all(selected.map(variant => this.fetchVariant(variant, this.controller.signal)));
        if (version !== this.version || !this.isConnected) return false;
        const byId = new Map(fresh.map(variant => [variant.id, variant]));
        const catalog = this.catalog.variants.map(variant => byId.get(String(variant.id)) || variant);
        const bundle = this.makeBundle(catalog);
        const total = this.pricingTotal(bundle, fresh);
        const currentCart = await this.fetchCart(this.controller.signal);
        if (version !== this.version || !this.isConnected) return false;
        const group = window.EverteeBundleCore.inspectCartGroup(currentCart, bundle.toCartPayload().items, total, this.dataset.currency);
        if (group.state !== 'absent') throw new Error('bundle-cart-mismatch');
        bundle.syncNativeForm(this.form);
        this.verifiedTotal = total;
        this.bundle = bundle;
        this.ready = true;
        text(this.status, '');
        text(this.querySelector('[data-owned-total]'), new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {style: 'currency', currency: this.dataset.currency}).format(total / 100));
        this.querySelector('[data-owned-total-wrap]').hidden = false;
        this.wallet.hidden = false;
        this.wallet.inert = false;
        this.walletPrompt.hidden = true;
        return true;
      } catch (error) {
        if (version !== this.version || error.name === 'AbortError') return false;
        this.lock();
        if (['bundle-cart-mismatch', 'bundle-add-uncertain'].includes(error.message)) { this.purchaseError(error); return false; }
        text(this.status, error.message === 'pricing-changed' ? this.say('pricingChanged') :
          error.message === 'pricing' ? this.say('pricingUnverified') :
          error.message === 'colour' ? this.say('chooseAll') :
          ['VARIANT_UNAVAILABLE', 'QUANTITY_RULE', 'VARIANT_NOT_FOUND'].includes(error.code) || ['variant', 'finish'].includes(error.message) ? this.say('unavailable') : this.say('retry'));
        return false;
      }
    }

    async onSubmit(event) {
      const submitter = event.submitter;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (submitter !== this.addButton) return;
      if (this.busy || !this.catalog) return;
      if (!this.size && this.pending?.state === 'prepared') { this.showSizeError(); return; }
      this.busy = true;
      this.setBusy(true);
      let mutationStarted = false;
      let confirmed = false;
      let destination;
      try {
        const target = this.dataset.cartUrl;
        if (!target) throw new Error('checkout-url');
        const next = new URL(target, location.origin);
        if (next.origin !== location.origin) throw new Error('checkout-url');
        destination = next.href;
        if (!this.ownedPricingActive()) throw new Error('pricing');
        if (await this.recoverAttempt()) {
          text(this.status, this.say('added'));
          this.navigateTo(destination);
          return;
        }
        if (!await this.refresh()) return;
        const payload = this.bundle.toCartPayload();
        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const redirect = typeof window.everteeShouldRedirectAfterAdd === 'function' && window.everteeShouldRedirectAfterAdd();
        if (cart && !redirect && typeof cart.getSectionsToRender === 'function') {
          payload.sections = cart.getSectionsToRender().map(section => section.id);
          payload.sections_url = location.pathname;
          cart.setActiveElement?.(document.activeElement);
        }
        this.wallet.hidden = true;
        this.wallet.inert = true;
        const url = new URL(this.dataset.cartAddUrl + '.js', location.origin);
        if (url.origin !== location.origin) throw new Error('cart-url');
        this.rememberAttempt(payload.items, this.verifiedTotal);
        mutationStarted = true;
        const response = await fetch(url.href, {
          method: 'POST', credentials: 'same-origin', headers: {'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest'}, body: JSON.stringify(payload)
        });
        if (!response.ok) {
          this.pending.state = response.status >= 400 && response.status < 500 ? 'rejected' : 'uncertain';
          this.savePending();
          throw new Error('cart-add');
        }
        const result = await response.json();
        if (!window.EverteeBundleCore.exactItems(result.items, payload.items)) throw new Error('cart-response');
        const currentCart = await this.fetchCart();
        const group = window.EverteeBundleCore.inspectCartGroup(currentCart, payload.items, this.verifiedTotal, this.dataset.currency);
        if (group.state !== 'exact') throw new Error(group.state === 'absent' ? 'bundle-add-uncertain' : 'bundle-cart-mismatch');
        confirmed = true;
        this.newPending();
        text(this.status, this.say('added'));
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          await publish(PUB_SUB_EVENTS.cartUpdate, {source:'evertee-owned-bundle',productVariantId:payload.items[0].id,cartData:result});
        }
        if (cart && !redirect && typeof cart.renderContents === 'function' && result.sections) {
          cart.classList.remove('is-empty');
          cart.renderContents({...result.items[0], ...result});
          await this.refresh();
        } else this.navigateTo(destination);
      } catch (error) {
        this.lock();
        if (confirmed) {
          this.navigateTo(destination);
          return;
        }
        if (mutationStarted) {
          if (this.pending.state !== 'rejected') this.pending.state = 'uncertain';
          try { this.savePending(); } catch (_) {}
          try {
            if (await this.recoverAttempt()) {
              text(this.status, this.say('added'));
              this.navigateTo(destination);
              return;
            }
          } catch (recoveryError) { error = recoveryError; }
          if (this.pending.state !== 'prepared') {
            this.purchaseError(error.message === 'bundle-cart-mismatch' ? error : new Error('bundle-add-uncertain'));
            return;
          }
        }
        this.purchaseError(error);
      } finally {
        this.busy = false;
        this.setBusy(false);
      }
    }

    navigateTo(destination) {
      location.assign(destination);
    }

    setBusy(busy) {
      this.setAttribute('aria-busy', String(busy));
      this.querySelectorAll('button').forEach(button => { button.disabled = busy; });
    }
  }
  if (!customElements.get('evertee-owned-bundle')) customElements.define('evertee-owned-bundle', EverteeOwnedBundle);
})();



